import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useLocales } from '@/locales';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

const PrivacyAgreement = ({
  onPrivacyChange,
  isVisible = false,
  privacyAgreed = false,
}) => {
  const { translate } = useLocales();
  const [expanded, setExpanded] = useState(true);

  const handleCheckChange = checked => {
    onPrivacyChange(checked);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="w-full rounded-lg border bg-card">
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="privacy-agreement"
              checked={privacyAgreed}
              onCheckedChange={handleCheckChange}
            />
            <Label
              htmlFor="privacy-agreement"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              {translate('agree_with_public_offer')}
            </Label>
          </div>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                'ml-2 p-2 transition-transform duration-200',
                expanded && 'rotate-180',
              )}
              aria-expanded={expanded}
              aria-label={
                expanded ? translate('collapse') : translate('expand')
              }
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <div className="px-4 pb-4 pt-0 border-t">
            <LanguageText />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const LanguageText = () => {
  const currentLang = localStorage.getItem('i18nextLng') || 'ru';

  const textContent = {
    en: {
      text: 'PUBLIC OFFER to grant the right use of the software product Automated Interactive System "OPTIMIZATION OF RESOURCE PLANNING OF FEA..."',
      downloads: {
        text: 'Download & Read More',
        url: 'https://eximerp.uz/public/documents/of_en.docx',
      },
    },
    tr: {
      text: 'Eximerp.uz web sitesinde yayınlanan Otomatik İnteraktif Sistemi yazılım ürününün kullanım hakkının verilmesi için "DIŞ EKONOMİK FAALİYETLER KAYNAK PLANLAMASININ OPTİMİZASYONU" HALKA ARZ',
      downloads: {
        text: 'İndirin & Daha Fazlasını Okuyun',
        url: 'https://eximerp.uz/public/documents/of_tr.doc',
      },
    },
    uz: {
      text: 'OMMAVIY OFERTA. Avtomatlashtirilgan interaktiv tizim dasturiy mahsulotidan foydalanish huquqini berish. "TASHQI IQTISODIY FAOLIYATNI RESURSLARNI REJALASHTIRISHNI OPTIMALLASHTIRISH..."',
      downloads: {
        text: "Yuklab oling va ko'proq o'qing",
        url: 'https://eximerp.uz/public/documents/of_uz.docx',
      },
    },
    ru: {
      text: 'ПУБЛИЧНАЯ ОФЕРТА на предоставление права пользования программным продуктом Автоматизированная Интерактивная Система «ОПТИМИЗАЦИЯ РЕСУРСНОГО ПЛАНИРОВАНИЯ ВЭД»...',
      downloads: {
        text: 'Скачать & Узнать больше',
        url: 'https://eximerp.uz/public/documents/of_ru.docx',
      },
    },
  };

  const content = textContent[currentLang] || textContent.ru;

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{content.text}</p>
      <Button variant="outline" className="w-full" asChild>
        <a
          target="_blank"
          href={content.downloads.url}
          rel="noopener noreferrer"
        >
          {content.downloads.text}
        </a>
      </Button>
    </div>
  );
};

PrivacyAgreement.propTypes = {
  onPrivacyChange: PropTypes.func.isRequired,
  isVisible: PropTypes.bool,
  privacyAgreed: PropTypes.bool,
};

export default PrivacyAgreement;
